import {
	createFileRoute,
	redirect,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { useEffect, useTransition } from "react";
import {
	fetchQuery,
	graphql,
	useLazyLoadQuery,
	useMutation,
	useRefetchableFragment,
} from "react-relay";
import { toast } from "sonner";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { environment } from "@/relay/environment";
import type { feeds_club$key } from "./__generated__/feeds_club.graphql";
import type { feedsLoaderQuery } from "./__generated__/feedsLoaderQuery.graphql";
import type { feedsOnboardingQuery } from "./__generated__/feedsOnboardingQuery.graphql";
import type { feedsOnboardingUpdateMutation } from "./__generated__/feedsOnboardingUpdateMutation.graphql";

export const Route = createFileRoute("/_logged/onboarding/$club/feeds")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const data = await fetchQuery<feedsLoaderQuery>(
			environment,
			graphql`
        query feedsLoaderQuery($club: String!) {
          club(slug: $club) {
            communityEnabled: configuration(key: "COMMUNITY_ENABLED")
          }
        }
      `,
			{
				club: params.club,
			},
		).toPromise();

		if (data?.club.communityEnabled === "0") {
			throw redirect({
				to: "/onboarding/$club/eventos",
				params: { club: params.club },
			});
		}

		return;
	},
});

function RouteComponent() {
	const { club: clubSlug } = useParams({ from: Route.id });
	const { club: onboardingClub } = useRouteContext({
		from: "/_logged/onboarding/$club",
	});
	const navigate = useNavigate({ from: Route.fullPath });
	const [_isPending, startTransition] = useTransition();
	const { capture } = useOnboardingAnalytics("feeds", clubSlug);

	const data = useLazyLoadQuery<feedsOnboardingQuery>(
		graphql`
      query feedsOnboardingQuery($slug: String!) {
          club(slug: $slug) {
            ...feeds_club
            leaderboardEnabled: configuration(key: "LEADERBOARD")
          }
      }
  	`,
		{
			slug: clubSlug,
		},
	);

	const [club, refetch] = useRefetchableFragment<
		feedsOnboardingQuery,
		feeds_club$key
	>(
		graphql`
      fragment feeds_club on Club
      @refetchable(queryName: "ClubFeedsRefetchQuery")
      {
          id
          isBootstrapCommunityRunning
          groups {
              id
              spaces {
                  id
                  name
                  slug
                  icon
              }
          }
      }
  	`,
		data.club,
	);

	const isBootstrapCommunityRunning = club?.isBootstrapCommunityRunning;

	const [updateClub] = useMutation<feedsOnboardingUpdateMutation>(graphql`
      mutation feedsOnboardingUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
        updateClub(id: $club, payload: $payload) {
          id
        }
      }
    `);

	const handleApprove = () => {
		const spaces = club.groups
			.filter(Boolean)
			.flatMap((group) => group.spaces.filter(Boolean));
		updateClub({
			variables: {
				club: onboardingClub?.id ?? "",
				payload: {
					configurations: [
						{
							key: "ONBOARDING_LAST_PAGE",
							value: "feeds",
						},
					],
				},
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				capture("completed", { feed_count: spaces.length });
				navigate({
					to: "/onboarding/$club/eventos",
					params: { club: clubSlug },
				});
			},
			onError: () => {
				toast.error("Erro ao salvar o progresso do onboarding.");
			},
		});
	};

	useEffect(() => {
		if (isBootstrapCommunityRunning) {
			const interval = setInterval(() => {
				startTransition(() => {
					refetch({}, { fetchPolicy: "network-only" });
				});
			}, 15000);

			return () => {
				clearInterval(interval);
			};
		}
	}, [isBootstrapCommunityRunning, refetch]);

	if (isBootstrapCommunityRunning) {
		return (
			<div className="w-full h-full min-h-[600px] flex flex-col gap-3 items-center justify-center p-6">
				<Spinner className="size-6" />
				<p className="text-lg">Configurando sua comunidade...</p>
			</div>
		);
	}

	const spaces = club.groups
		.filter(Boolean)
		.flatMap((group) => group.spaces.filter(Boolean));

	const defaultSpaces = [
		"Se apresente!",
		"Comunicados",
		"Dúvidas",
		"Debates",
		"Vitórias",
		"Materiais",
	];

	const isDefaultSpaces = spaces
		.map((s) => s.name)
		.every((name, index) => defaultSpaces[index] === name);

	return (
		<div className="container mx-auto p-6 max-w-2xl flex flex-col">
			<Card className="p-6 gap-0">
				<div>
					<Heading>Estrutura de Feeds</Heading>

					<Text>
						{isDefaultSpaces
							? "Essa é a estrutura padrão. Você pode mudar depois."
							: "IA sugeriu estes canais baseados no seu nicho:"}
					</Text>
				</div>

				{spaces.length && (
					<div className="mt-6 flex flex-col gap-3">
						{spaces.map((space) => (
							<div
								key={space.id}
								className="flex items-center gap-3 p-3 rounded-md border min-h-12"
							>
								{space.icon && <span className="shrink-0">{space.icon}</span>}
								<span className="font-medium">{space.name}</span>
							</div>
						))}
					</div>
				)}

				<Separator className="mt-6" />

				<div className="py-6 pb-0 flex items-center justify-between">
					<div className="flex items-start justify-start gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() =>
								navigate({
									to: "/onboarding/$club/cursos",
									params: { club: clubSlug },
								})
							}
						>
							Voltar
						</Button>
					</div>

					<div className="flex items-center gap-3">
						<Button type="button" onClick={handleApprove}>
							Aprovar Estrutura
						</Button>
					</div>
				</div>
			</Card>
		</div>
	);
}
